/**
 * The drawing kit every hero demo deck is written with.
 *
 * Each slide is an SVG string in its own 1280 x 720 space. One source serves the preview (inline in the
 * page), the morph loader's target texture and the directory's thumbnails (both rasterised through
 * `rasterSvg.ts`, which inlines the fonts and photos a slide points at, so all three render the same).
 *
 * Type comes from six faces, each declared inside the slide that uses it: Inter for text, Inter Display
 * for larger sans, Instrument Serif for editorial headlines, JetBrains Mono for technical labels, and
 * Manrope with Source Code Pro for the deck styled after Supabase.
 */

export const SLIDE_W = 1280;
export const SLIDE_H = 720;

export const SANS = "'Deck Demo Sans', Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif";
export const DISPLAY = "'Deck Demo Display', 'Inter Display', Inter, 'Helvetica Neue', Arial, sans-serif";
export const SERIF = "'Deck Demo Serif', 'Instrument Serif', Georgia, 'Times New Roman', serif";
export const MONO = "'Deck Demo Mono', 'JetBrains Mono', Menlo, Consolas, monospace";
/** Supabase's pair, for the deck styled after it: Manrope headlines, Source Code Pro code. */
export const MANROPE = "'Deck Demo Manrope', Manrope, 'Helvetica Neue', Arial, sans-serif";
export const CODE = "'Deck Demo Code', 'Source Code Pro', Menlo, Consolas, monospace";
/** Kept for the callers that predate the four families. */
export const FONT = SANS;

/** Every face a slide may ask for. Only the families a slide actually names are declared in it. */
const FACES: { family: string; weight: string; style?: 'italic'; url: string }[] = [
  { family: 'Deck Demo Sans', weight: '400', url: '/fonts/Inter-Regular.woff2' },
  { family: 'Deck Demo Sans', weight: '500', url: '/fonts/Inter-Medium.woff2' },
  { family: 'Deck Demo Sans', weight: '600', url: '/fonts/Inter-SemiBold.woff2' },
  { family: 'Deck Demo Display', weight: '400', url: '/fonts/InterDisplay-Regular.woff2' },
  { family: 'Deck Demo Display', weight: '500', url: '/fonts/InterDisplay-Medium.woff2' },
  { family: 'Deck Demo Display', weight: '600', url: '/fonts/InterDisplay-SemiBold.woff2' },
  { family: 'Deck Demo Serif', weight: '400', url: '/landing/hero-decks/fonts/InstrumentSerif-Regular.woff2' },
  { family: 'Deck Demo Serif', weight: '400', style: 'italic', url: '/landing/hero-decks/fonts/InstrumentSerif-Italic.woff2' },
  { family: 'Deck Demo Mono', weight: '400 600', url: '/landing/hero-decks/fonts/JetBrainsMono.woff2' },
  { family: 'Deck Demo Manrope', weight: '400 700', url: '/landing/hero-decks/fonts/Manrope.woff2' },
  { family: 'Deck Demo Code', weight: '400 600', url: '/landing/hero-decks/fonts/SourceCodePro.woff2' },
];

/** The photographs the decks may place. */
export const PHOTO = {
  cliff: '/landing/hero-decks/photos/cliff.jpg',
  desert: '/landing/hero-decks/photos/desert.jpg',
  arch: '/landing/hero-decks/photos/arch.jpg',
  interior: '/landing/hero-decks/photos/interior.jpg',
  stairs: '/landing/hero-decks/photos/stairs.jpg',
  cylinder: '/landing/hero-decks/photos/cylinder.jpg',
  redcurve: '/landing/hero-decks/photos/redcurve.jpg',
} as const;

const INK = '#121212';

export interface DemoSlide {
  /** Unique across every demo: it keys the morph texture cache. */
  id: string;
  title: string;
  svg: string;
}

export const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * A slide's tracking, written so the page cannot change it.
 *
 * The workspace chrome sets `letter-spacing: 0.48px !important` on every element under its theme
 * (`HeaderTheme.module.css`), and a CSS rule always beats an SVG attribute. Inline in the preview the
 * slides wore the chrome's tracking while their raster kept their own, so every line changed width the
 * moment the morph lifted. An inline `!important` style is the one declaration that out-ranks it, and
 * the raster reads the same markup, so the two can no longer differ.
 */
const tracked = (tracking: string) => `letter-spacing:${tracking} !important`;

/** Text with no tracking of its own is the slide's, not the chrome's; a line's spans inherit its text's. */
const TRACKING_GUARD =
  '<style>svg[data-demo-slide] text{letter-spacing:normal !important}svg[data-demo-slide] tspan{letter-spacing:inherit !important}</style>';

export interface LinesOpts {
  fill?: string;
  weight?: number;
  anchor?: 'start' | 'middle' | 'end';
  tracking?: string;
  font?: string;
  italic?: boolean;
  opacity?: number;
}

/** A block of lines as one `<text>`: `x` is the anchor, `y` the first baseline. */
export function lines(x: number, y: number, size: number, leading: number, text: string[], opts: LinesOpts = {}): string {
  const { fill = INK, weight = 400, anchor = 'start', tracking = '-0.02em', font = SANS, italic = false, opacity } = opts;
  const spans = text
    .map((t, i) => `<tspan x="${x}" ${i === 0 ? `y="${y}"` : `dy="${leading}"`}>${esc(t)}</tspan>`)
    .join('');
  return `<text font-family="${font}" font-size="${size}" font-weight="${weight}"${italic ? ' font-style="italic"' : ''} fill="${fill}" text-anchor="${anchor}" style="${tracked(tracking)}"${opacity !== undefined ? ` opacity="${opacity}"` : ''}>${spans}</text>`;
}

/** One line of text. */
export const text = (x: number, y: number, size: number, str: string, opts: LinesOpts = {}) => lines(x, y, size, size, [str], opts);

/** Body copy: 18 on 26, the reading size every demo deck shares. */
export const body = (x: number, y: number, text: string[], fill: string = INK) =>
  lines(x, y, 18, 26, text, { fill, tracking: '-0.01em' });

/** The running header: deck, section, page, in 14px. */
export function running(deck: string, section: string, page: number, fill: string): string {
  return (
    lines(48, 50, 14, 18, [deck], { fill, tracking: '0' }) +
    lines(300, 50, 14, 18, [section], { fill, tracking: '0' }) +
    lines(1232, 50, 14, 18, [String(page).padStart(2, '0')], { fill, anchor: 'end', tracking: '0' })
  );
}

export const rect = (x: number, y: number, w: number, h: number, fill: string, r = 0) =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${fill}"/>`;

/** A rectangle drawn as an outline. */
export const frame = (x: number, y: number, w: number, h: number, stroke: string, r = 0, width = 1) =>
  `<rect x="${x + width / 2}" y="${y + width / 2}" width="${w - width}" height="${h - width}" rx="${r}" fill="none" stroke="${stroke}" stroke-width="${width}"/>`;

export const circle = (cx: number, cy: number, r: number, fill: string) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"/>`;

export const line = (x1: number, y1: number, x2: number, y2: number, stroke: string, width = 1, dash?: string) =>
  `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`;

export const path = (d: string, fill: string, stroke?: string, width = 1) =>
  `<path d="${d}" fill="${fill}"${stroke ? ` stroke="${stroke}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"` : ''}/>`;

let clipSeq = 0;

/** A photograph filling its box, cropped to cover, with rounded corners when `r` is set. */
export function photo(href: string, x: number, y: number, w: number, h: number, r = 0): string {
  clipSeq += 1;
  const id = `demo-photo-${clipSeq}`;
  return (
    `<clipPath id="${id}"><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}"/></clipPath>` +
    `<image href="${href}" x="${x}" y="${y}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${id})"/>`
  );
}

/** The `@font-face` rules for exactly the families this slide names. */
function faces(content: string): string {
  const used = FACES.filter((face) => content.includes(face.family));
  if (!used.length) return '';
  const rules = used
    .map((face) => `@font-face{font-family:'${face.family}';font-weight:${face.weight};font-style:${face.style ?? 'normal'};src:url(${face.url}) format('woff2');}`)
    .join('');
  return `<defs><style>${rules}</style></defs>`;
}

export const wrap = (content: string) => {
  // Raw `letter-spacing` attributes drawn outside `lines()` get the same protection.
  const guarded = content.replace(/ letter-spacing="([^"]*)"/g, (_, tracking: string) => ` style="${tracked(tracking)}"`);
  return `<svg xmlns="http://www.w3.org/2000/svg" data-demo-slide="" viewBox="0 0 ${SLIDE_W} ${SLIDE_H}" width="100%" height="100%">${TRACKING_GUARD}${faces(guarded)}${guarded}</svg>`;
};

/** The same slide as a CSS `image` value: the fallback chip until its raster exists. */
export function slideThumbnail(slide: DemoSlide): string {
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(slide.svg)}")`;
}
